// src/components/user-menu.tsx
'use client'

import Link from 'next/link'
import { signOut } from 'next-auth/react'
import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

type Plan = 'FREE' | 'PRO' | 'PREMIUM'

/**
 * Tailwind class for the avatar wrapper, keyed by tier. The avatar
 * gets a tier-specific ring color so Premium / Pro status is
 * reinforced visually on every page — not just by the PlanBadge
 * pill but by the avatar itself.
 *
 * For PREMIUM we use a gradient ring (cyan→violet) implemented as
 * a 2px gradient background on the wrapper, with the avatar
 * content covering the center; that leaves a 2px gradient halo.
 * Tailwind's `ring-*` utilities can't do gradients directly, hence
 * the wrapper trick.
 *
 * For PRO we use the standard ring utility in cyan. FREE / unknown
 * gets a plain border (no tier signaling).
 *
 * Mapping is centralized here so changing tier colors later is one
 * edit, not a grep job.
 */
function avatarWrapperClass(plan: Plan | undefined): string {
  if (plan === 'PREMIUM') {
    return 'p-[2px] bg-brand'
  }
  if (plan === 'PRO') {
    return 'p-[2px] ring-2 ring-brand/70 ring-offset-1 ring-offset-background'
  }
  return 'border'
}

// A school member's way back to their classes. /school is where a student sees
// the classes they're in and where teachers and school admins run theirs, and
// until this link nothing in the signed-in chrome led there: staff had to type
// the URL or dig out a digest email after their first sign-in.
const SCHOOL_LINK = { href: '/school', label: 'Your classes' } as const

// Account / social links that used to live in the NavLinks "More" dropdown.
// They were pulled out of the top bar (nav declutter) and folded into this
// avatar menu so the app nav stays lean. Grouped into account settings vs.
// social so the menu stays scannable; Notifications + Accommodations are kept
// here because they have no other home in the chrome (and Accommodations —
// extended time etc. — matters for SAT students).
const ACCOUNT_LINKS = [
  { href: '/settings', label: 'Settings' },
  { href: '/settings/notifications', label: 'Notifications' },
  { href: '/settings/accommodations', label: 'Accommodations' },
  { href: '/settings/study', label: 'Study preferences' },
] as const

// Personal study surfaces — yours, not shared. Bookmarks moved here from the
// Tests dropdown when that dropdown was removed: saved QUESTIONS are personal
// and have nothing to do with picking a test to sit.
const PERSONAL_LINKS = [{ href: '/bookmarks', label: 'Bookmarks' }] as const

const SOCIAL_LINKS = [
  { href: '/groups', label: 'My Groups' },
  { href: '/leaderboard', label: 'Leaderboard' },
  { href: '/badges', label: 'Badges' },
  { href: '/referral', label: 'Invite Friends' },
] as const

// Marketing/company pages. The nav redesign dropped these from the signed-in top
// bar; keep them reachable in-app here (a logged-in user shouldn't have to log
// out to find About or the Blog).
const COMPANY_LINKS = [
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
  // Help and Contact BOTH exist and are linked from the marketing footer — but
  // that footer renders on the dashboard and the marketing pages only. A signed-in
  // student on /practice, /study-plan, /learn or /settings had no route to
  // support anywhere in the app without signing out or guessing the URL.
  { href: '/help', label: 'Help' },
  { href: '/contact', label: 'Contact us' },
] as const

function planLabel(plan: Plan | undefined): string {
  if (plan === 'PREMIUM') return 'Premium plan'
  if (plan === 'PRO') return 'Pro plan'
  return 'Free plan'
}

export default function UserMenu({
  name,
  image,
  plan,
  schoolMember = false,
}: {
  name?: string | null
  image?: string | null
  plan?: Plan
  /** Belongs to a school in any role (student, teacher or school admin). */
  schoolMember?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isGradient = plan === 'PREMIUM'

  // Close on outside click + Escape (mirrors the NavLinks dropdown pattern —
  // the repo has no shared DropdownMenu primitive). This is a disclosure of
  // links, not a role=menu widget, so Tab navigates the items; Escape closes
  // and returns focus to the trigger so keyboard users don't lose their place.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen((prev) => {
          if (prev) triggerRef.current?.focus()
          return false
        })
      }
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [])

  // Dropdown items live inside .nav-gradient but the panel opts out of the
  // gradient's white-text cascade (see globals.css [data-nav-popover]).
  const itemClass =
    'block rounded px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted hover:text-accent-foreground'

  return (
    <div className="relative" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Account menu"
        className={`flex items-center gap-1 rounded-full text-white/90 transition-colors hover:text-white`}
        title={`${(name ?? '').split(' ')[0] || 'Account'} — account menu`}
      >
        <span
          className={`block size-8 overflow-hidden rounded-full bg-muted ${avatarWrapperClass(plan)}`}
        >
          {/* For the Premium gradient ring, the avatar content sits inside a 2px
              padding from the wrapper. Rounding the inner div + giving it a bg
              matching the page keeps the gradient halo visible. */}
          <span
            className={`grid size-full place-items-center overflow-hidden ${isGradient ? 'rounded-full bg-background' : ''}`}
          >
            {/* Use <Image> if the URL is whitelisted in next.config */}
            {image ? (
              <img
                src={image}
                alt=""
                loading="lazy"
                decoding="async"
                className="size-full object-cover"
              />
            ) : (
              <span className="grid size-full place-items-center text-xs">👤</span>
            )}
          </span>
        </span>
        <ChevronDown
          className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          data-nav-popover
          className="absolute right-0 top-full z-50 mt-2 w-56 rounded-md border border-border bg-card p-1 shadow-lg"
        >
          <div className="px-3 py-2">
            <p className="truncate text-sm font-medium text-foreground">{name || 'Your account'}</p>
            <p className="text-xs text-muted-foreground">{planLabel(plan)}</p>
          </div>
          <div className="my-1 border-t border-border" />
          {schoolMember && (
            <>
              <Link href={SCHOOL_LINK.href} className={itemClass} onClick={() => setOpen(false)}>
                {SCHOOL_LINK.label}
              </Link>
              <div className="my-1 border-t border-border" />
            </>
          )}
          {ACCOUNT_LINKS.map((m) => (
            <Link key={m.href} href={m.href} className={itemClass} onClick={() => setOpen(false)}>
              {m.label}
            </Link>
          ))}
          <div className="my-1 border-t border-border" />
          {PERSONAL_LINKS.map((m) => (
            <Link key={m.href} href={m.href} className={itemClass} onClick={() => setOpen(false)}>
              {m.label}
            </Link>
          ))}
          <div className="my-1 border-t border-border" />
          {SOCIAL_LINKS.map((m) => (
            <Link key={m.href} href={m.href} className={itemClass} onClick={() => setOpen(false)}>
              {m.label}
            </Link>
          ))}
          <div className="my-1 border-t border-border" />
          {COMPANY_LINKS.map((m) => (
            <Link key={m.href} href={m.href} className={itemClass} onClick={() => setOpen(false)}>
              {m.label}
            </Link>
          ))}
          <div className="my-1 border-t border-border" />
          {/* Replays the per-plan welcome tour. The ?tour=1 param makes
              WelcomeTour (mounted on the dashboard) open regardless of the
              "already seen" state; it strips the param again on close. */}
          <Link href="/dashboard?tour=1" className={itemClass} onClick={() => setOpen(false)}>
            Take the product tour
          </Link>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            onClick={() => signOut({ callbackUrl: '/', redirect: true })}
            className={`${itemClass} w-full text-left`}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
